<script lang="ts" setup>
import { ValidatorType } from '@/helper/ValidateDirective'
import FormElement from '@/components/FormElement.vue'
import InputSuffix from '@/components/InputSuffix.vue'
import formStore from '@/store/FormStore'
import projectStore from '@/store/ProjectStore'
import userStore from '@/store/UserStore'
import { computed, onMounted } from 'vue'
import { ComparisonFuelType, FuelType } from '@/helper/Types'
import { capitalizeFirstLetter } from '@/helper/tools'
import ComboBox from '@/components/ComboBox.vue'
import i18n, { formatLanguage } from '@/helper/Translations'

const props = defineProps<{
  validator: ValidatorType
}>()

defineExpose({
  validator: props.validator
})

onMounted(() => {
  if (!formStore.data.comparisonFuelTypes || (formStore.data.comparisonFuelTypes as ComparisonFuelType[]).length === 0) {
    addComparisonRow(userStore.referenceFuelTypes[0].fuelType)
  }
})

const comparisonFuelTypes = computed<ComparisonFuelType[]>(() => {
  return (formStore.data.comparisonFuelTypes as ComparisonFuelType[]) || []
})

function availableFuelTypes(selectedFuelType?: FuelType): { label: string; value: FuelType }[] {
  return userStore.referenceFuelTypes
    .filter(r => !comparisonFuelTypes.value.some(c => c.fuelType === r.fuelType) || r.fuelType === selectedFuelType)
    .map(f => ({
      label: i18n.global.t(`fuel_types.${f.fuelType}`),
      value: f.fuelType
    }))
}

function addComparisonRow(fuelType: FuelType): void {
  const comparisonFuelType = userStore.referenceFuelTypes.find(c => c.fuelType === fuelType)
  if (comparisonFuelType) {
    (formStore.data.comparisonFuelTypes as ComparisonFuelType[]).push({
      fuelType: comparisonFuelType.fuelType,
      price: formatEnergyPrice(comparisonFuelType.price)
    })
  }
}

function removeComparisonRow(fuelType: ComparisonFuelType): void {
  const index = comparisonFuelTypes.value.indexOf(fuelType)
  comparisonFuelTypes.value.splice(index, 1)
}

function formatEnergyPrice(price: number): string {
  const result = userStore.enableIrregularCurrency ? price : price * 100
  return result.toLocaleString(formatLanguage(), { maximumFractionDigits: 6 })
}

function getDefaultPrice(fuelType: FuelType): number | undefined {
  return userStore.referenceFuelTypes.find(a => a.fuelType === fuelType)?.price
}
</script>

<template>
  <FormElement v-validate="'required'" class="energy-prices-wrapper">
    <div class="fuel-type-row">
      <div class="report-type-select__headline">
        {{ $t('modal.download_configuration_electricity_price') }}
      </div>

      <FormElement
        v-validate="projectStore.projectProperties.electricityPrice.formValidationRule"
        :error="validator.electricityPrice?.error"
      >
        <InputSuffix
          id="electricity-price"
          key="electricity-price"
          v-model="formStore.data.electricityPrice"
          :placeholder="$t('modal.download_configuration_placeholder_electricity_price')"
          :reset-value="formatEnergyPrice(userStore.electricityPrice).toString()"
          :suffix="$t('global.electricity_price_unit')"
        />
      </FormElement>
    </div>

    <div v-if="userStore.activeFuelTypeOptions && userStore.enableHeatingCostComparison" class="fuel-type-wrapper">
      <div v-for="comparisonFuelType in comparisonFuelTypes" :key="comparisonFuelType.fuelType" class="fuel-type-row">
        <FormElement
          v-validate="projectStore.projectProperties.comparisonFuelTypes.formValidationRule?.fuelType"
          :error="validator[`comparisonFuelType${capitalizeFirstLetter(comparisonFuelType.fuelType.toLowerCase())}`]?.error"
        >
          <ComboBox
            :id="`comparison-fuel-type-${comparisonFuelType.fuelType.toLowerCase()}`"
            v-model="comparisonFuelType.fuelType"
            :options="availableFuelTypes(comparisonFuelType.fuelType)"
            @change="() => (comparisonFuelType.price = formatEnergyPrice(getDefaultPrice(comparisonFuelType.fuelType)))"
          />
        </FormElement>

        <FormElement
          v-validate="projectStore.projectProperties.comparisonFuelTypes.formValidationRule?.price"
          :error="validator[`comparisonFuelTypePrice${capitalizeFirstLetter(comparisonFuelType.fuelType.toLowerCase())}`]?.error"
        >
          <InputSuffix
            :id="`comparison-fuel-type-price-${comparisonFuelType.fuelType.toLowerCase()}`"
            v-model="comparisonFuelType.price"
            :placeholder="$t('modal.download_configuration_placeholder_fueltype_price')"
            :reset-value="formatEnergyPrice(getDefaultPrice(comparisonFuelType.fuelType)).toString()"
            :suffix="$t('global.fueltype_price_unit')"
          />
        </FormElement>

        <button
          :disabled="comparisonFuelTypes.length < 2"
          class="icon-btn is--delete"
          type="button"
          @click="removeComparisonRow(comparisonFuelType)"
        >
          <i class="ic-trash" />
        </button>
      </div>
    </div>

    <div
      v-if="userStore.activeFuelTypeOptions.length > 1 && userStore.enableHeatingCostComparison"
      class="button-wrapper"
    >
      <button
        id="btn-add-comparison-fueltype"
        :disabled="comparisonFuelTypes.length > userStore.activeFuelTypeOptions.length - 1"
        class="btn is--tertiary has--icon-right"
        type="button"
        @click="addComparisonRow(availableFuelTypes()[0].value)"
      >
        {{ $t('step_download_page.btn_add_fueltype') }}
        <i class="ic-plus" />
      </button>
    </div>
  </FormElement>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/variables';

.energy-prices-wrapper {
  border: 1px solid $borderColor;
  border-radius: rem-calc(12);
  margin-top: rem-calc(12);

  .fuel-type-wrapper,
  .fuel-type-row {
    width: 100%;
  }

  .fuel-type-row {
    display: grid;
    grid-gap: rem-calc(16);
    grid-template-columns: 1fr 1fr 0.1fr;
    align-items: start;
    padding: rem-calc(16);
    border-bottom: 1px solid $borderColorLight;

    &:last-child {
      border-bottom: 0;
    }

    .form__element {
      margin-bottom: 0;
    }

    .is--delete,
    .report-type-select__headline {
      height: 40px;
    }

    .report-type-select__headline {
      display: flex;
      align-items: center;
    }
  }

  .button-wrapper {
    border-top: 1px solid $borderColorLight;
    width: 100%;
    margin: 0;

    #btn-add-comparison-fueltype {
      margin: rem-calc(16);
    }
  }

  :deep(.form__input-wrapper) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .inputsuffix__wrapper {
      width: 100%;
    }
  }
}
</style>
